import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCookiesLogin } from '../../config/cookies';
import Auth from '../../services/auth';
import { protocolMessages } from '../../config/protocolMessages';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import { Backdrop, CircularProgress } from '@mui/material';

export function FormCard({ title, children }) {
  return (
    <div className="w-full max-w-md border border-gray-200 rounded-lg p-10">
      <h1 className="text-gray-900 text-2xl font-bold mb-6">{title}</h1>
      {children}
    </div>
  );
}

export function AuthInput({
  label,
  id,
  type = 'text',
  placeholder,
  value,
  onChange,
  onKeyDown,
  rightSlot,
  focusBorder = 'focus:border-[#83d84d]',
}) {
  return (
    <div className="mb-5">
      {label && (
        <label htmlFor={id} className="block text-gray-700 text-sm font-medium mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className={`w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder:text-gray-400 text-sm outline-none transition-colors ${focusBorder} focus:ring-2 focus:ring-[#83d84d]/20 ${rightSlot ? 'pr-12' : ''}`}
        />
        {rightSlot && <div className="absolute right-3 top-1/2 -translate-y-1/2">{rightSlot}</div>}
      </div>
    </div>
  );
}

export function PrimaryButton({ onClick, disabled, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full bg-[#83d84d] hover:bg-[#72c73d] active:bg-[#62b82d] text-black font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
    >
      {children}
    </button>
  );
}

export function LoginForm() {
  const { t } = useTranslation();
  const { cookies, setCookies } = useCookiesLogin();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const auth = new Auth(username, password);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleLogin();
    }
  };

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      const response = await auth.login();
      if (response.status === 200) {
        const { status } = await auth.store();
        if (status === 200) {
          setCookies('AUTH_TOKEN', response.data);
          window.location.href = "/app/dashboard";
        }
      } else if (response.status === 401) {
        const message = protocolMessages[401] || t('auth.maintenance');
        toast.error(message, { theme: 'dark' });
      } else {
        const message = protocolMessages[response.status] || t('auth.maintenance');
        toast.warning(message, { theme: 'dark' });
      }
    } catch (error) {
      toast.error(t('auth.loginError'), { theme: 'dark' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormCard title={t('auth.accessAcount')}>
      <AuthInput
        label={t('auth.user')}
        id="loginUser"
        type="text"
        placeholder={t('auth.userPlaceholder')}
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        focusBorder="focus:border-[#83d84d]"
      />

      <div>
        <AuthInput
          label={t('auth.password')}
          id="loginPassword"
          type={showPassword ? 'text' : 'password'}
          placeholder={t('auth.passwordPlaceholder')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
          rightSlot={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-600 transition-colors mt-2"
            >
              {showPassword ? <EyeOff size={24} /> : <Eye size={24} />}
            </button>
          }
        />
      </div>

      <div className="flex justify-end mb-5">
        <Link
          to="/recoverPassword"
          className="text-gray-400 hover:text-gray-600 text-sm transition-colors"
        >
          {t('auth.forgotPassword')}
        </Link>
      </div>

      <PrimaryButton onClick={handleLogin} disabled={isLoading}>
        {isLoading ? t('common.loggingIn') : t('common.login')}
      </PrimaryButton>

      <p className="text-center text-gray-500 text-sm mt-5">
        {t('auth.noAccount')}{' '}
        <Link
          to="/register"
          className="text-[#83d84d] hover:text-[#72c73d] font-medium transition-colors"
        >
          {t('auth.register')}
        </Link>
      </p>

      <Backdrop open={isLoading} style={{ zIndex: 9999 }}>
        <CircularProgress color="inherit" sx={{ color: '#83d84d' }} />
      </Backdrop>
    </FormCard>
  );
}
